import type { CompositeFilterDescriptor, DataSourceRequestState, SortDescriptor } from "@progress/kendo-data-query";
import { DropDownList, type DropDownListChangeEvent } from "@progress/kendo-react-dropdowns";
import { GridColumn, GridNoRecords, type GridCellProps, type GridCustomCellProps, type GridDataStateChangeEvent, type GridDetailRowProps, type GridPageChangeEvent, type GridSortChangeEvent } from "@progress/kendo-react-grid";
import { NumericTextBox, type NumericTextBoxChangeEvent } from "@progress/kendo-react-inputs";
import { skipToken } from "@reduxjs/toolkit/query";
import { useState } from "react";
import { useListAdminChatConversationMessagesQuery, useListAdminChatConversationsQuery } from "../../api/admin/adminChatConversationApi";
import { useListCustomersQuery } from "../../api/admin/adminCustomerApi";
import { useListUsersQuery } from "../../api/admin/adminUserApi";
import AppGrid from "../../components/grid/appGrid";
import { ChatMessageType, type AdminChatConversationDto, type AdminChatConversationMessageDto, type CustomerDto, type UserDto } from "../../contracts";
import { getChatMessageTypeLabel } from "../../utils/chatMessageUtils";
import { formatDateTime, toDate } from "../../utils/dateValueConversion";
import { truncateString } from "../../utils/stringValueConversion";
import { Button } from "@progress/kendo-react-buttons";

const initialSort: SortDescriptor[] = [{ field: "lastMessageAt", dir: "desc" }];
const messageInitialSort: SortDescriptor[] = [{ field: "sequence", dir: "asc" }];
const selectorRequest: DataSourceRequestState = { skip: 0, take: 1000, sort: [{ field: "name", dir: "asc" }] };
const userSelectorRequest = (customer: CustomerDto | null): DataSourceRequestState | typeof skipToken =>
    customer == null
        ? skipToken
        : {
                skip: 0, take: 1000, sort: [{ field: "email", dir: "asc" }],
                filter: {
                    logic: "and",
                    filters: [{ field: "customerId", operator: "eq", value: customer.id }]
                }
            };

const buildFilter = (conversationId: number | null, customer: CustomerDto | null, user: UserDto | null, dataStateFilter?: CompositeFilterDescriptor): CompositeFilterDescriptor => ({
    logic: "and",
    filters: [
        ...(dataStateFilter == null ? [] : [dataStateFilter]),
        ...(conversationId == null ? [] : [{ field: "id", operator: "eq", value: conversationId }]),
        ...(customer == null ? [] : [{ field: "user.customerId", operator: "eq", value: customer.id }]),
        ...(user == null ? [] : [{ field: "userId", operator: "eq", value: user.id }])
    ]
});

const DateCell = <T extends object>(field: keyof T): React.FC<GridCellProps> => props => {
    const value = (props.dataItem as T)[field] as Date | string | null | undefined;
    const date = toDate(value);
    return <td className="k-table-td">{date == null ? "-" : formatDateTime(date)}</td>;
};

const ChatMessageTypeCell = (props: GridCellProps): React.ReactElement => (
    <td>{getChatMessageTypeLabel(props.dataItem.type as ChatMessageType)}</td>
);

const TextCell = (props: GridCustomCellProps): React.ReactElement => {
    const text = (props.dataItem as AdminChatConversationMessageDto).text ?? "";

    return (
        <td className="k-table-td">
            <div className="d-flex align-items-center justify-content-between gap-2">
                <span>{truncateString(text, 60) || "-"}</span>
                {text && (
                    <Button
                        size="small"
                        themeColor="primary"
                        onClick={() => void navigator.clipboard.writeText(text)}
                    >
                        Copy
                    </Button>
                )}
            </div>
        </td>
    );
};

const JsonCell = (props: GridCustomCellProps): React.ReactElement => {
    const json = (props.dataItem as AdminChatConversationMessageDto).json ?? "";

    return (
        <td className="k-table-td">
            <div className="d-flex align-items-center justify-content-between gap-2">
                <span>{truncateString(json, 40) || "-"}</span>
                {json && (
                    <Button
                        size="small"
                        themeColor="primary"
                        onClick={() => void navigator.clipboard.writeText(json)}
                    >
                        Copy
                    </Button>
                )}
            </div>
        </td>
    );
};

const ConversationMessages: React.FC<GridDetailRowProps> = props => {
    const conversation = props.dataItem as AdminChatConversationDto;
    const [dataState, setDataState] = useState<DataSourceRequestState>({ skip: 0, take: 10, sort: messageInitialSort });
    const { data, isFetching, isError } = useListAdminChatConversationMessagesQuery({ ...dataState, chat_conversation_id: conversation.id });

    return (
        <section className="bg-body-tertiary p-3">
            <div className="card border-0 shadow-sm">
                <div className="card-header bg-body d-flex justify-content-between align-items-center">
                    <div>
                        <h3 className="h6 mb-0">Messages</h3>
                        <span className="small text-body-secondary">
                            Conversation #
                            {conversation.id}
                        </span>
                    </div>
                    {isError && <span className="badge text-bg-danger">Unable to load messages</span>}
                </div>
                <div className="card-body">
                    <AppGrid
                        showLoader={isFetching}
                        dataItemKey="id"
                        data={data?.data ?? []}
                        sortable={true}
                        filterable={false}
                        resizable={true}
                        initialState={dataState}
                        onPageChange={(event: GridPageChangeEvent) => setDataState(previous => ({ ...previous, skip: event.page.skip, take: event.page.take }))}
                        onDataStateChange={(event: GridDataStateChangeEvent) => setDataState({ ...event.dataState, skip: event.dataState.skip, take: event.dataState.take })}
                        sort={dataState.sort}
                        total={data?.total ?? 0}
                        skip={dataState.skip}
                        take={dataState.take}
                        defaultSort={messageInitialSort}
                        onSortChange={(event: GridSortChangeEvent) => setDataState(previous => ({ ...previous, sort: event.sort, skip: 0 }))}
                        pageable={{ buttonCount: 3, pageSizes: [10, 25, 50] }}
                    >
                        <GridNoRecords>{!isFetching && <span className="text-muted">{isError ? "Unable to load messages. Please try again." : "No messages found for this conversation."}</span>}</GridNoRecords>
                        <GridColumn field="sequence" title="Sequence" width="120px" />
                        <GridColumn field="type" title="Type" cells={{ data: ChatMessageTypeCell }} width="140px" />
                        <GridColumn field="action" title="Action" width="180px" cells={{ data: p => <td className="k-table-td">{(p.dataItem as AdminChatConversationMessageDto).action || "-"}</td> }} />
                        <GridColumn title="Text" cells={{ data: TextCell }} />
                        <GridColumn title="Json" cells={{ data: JsonCell }} />
                        <GridColumn field="createdAt" title="Created" width="220px" cells={{ data: DateCell<AdminChatConversationMessageDto>("created_at") }} />
                    </AppGrid>
                </div>
            </div>
        </section>
    );
};

const ChatConversationManagement: React.FC = () => {
    const [conversationId, setConversationId] = useState<number | null>(null);
    const [selectedCustomer, setSelectedCustomer] = useState<CustomerDto | null>(null);
    const [selectedUser, setSelectedUser] = useState<UserDto | null>(null);
    const [dataState, setDataState] = useState<DataSourceRequestState>({ skip: 0, take: 50, sort: initialSort });
    const request = { ...dataState, filter: buildFilter(conversationId, selectedCustomer, selectedUser, dataState.filter) };
    const { data, isFetching, isError } = useListAdminChatConversationsQuery(request);
    const { data: customerData, isFetching: isCustomersFetching, isError: isCustomersError } = useListCustomersQuery(selectorRequest);
    const { data: userData, isFetching: isUsersFetching, isError: isUsersError } = useListUsersQuery(userSelectorRequest(selectedCustomer));
    const resetPaging = (): void => setDataState(previous => ({ ...previous, skip: 0 }));

    const handleConversationIdChange = (event: NumericTextBoxChangeEvent): void => {
        setConversationId(event.value);
        resetPaging();
    };

    const handleCustomerChange = (event: DropDownListChangeEvent): void => {
        const customer = event.value as CustomerDto | null;
        setSelectedCustomer(customer?.id === 0 ? null : customer);
        setSelectedUser(null);
        resetPaging();
    };

    const handleUserChange = (event: DropDownListChangeEvent): void => {
        const user = event.value as UserDto | null;
        setSelectedUser(user?.id == null ? null : user);
        resetPaging();
    };

    return (
        <div className="d-flex flex-column gap-3 p-3">
            <div className="row g-3 align-items-end">
                <div className="col-12 col-md-3">
                    <label className="form-label fw-semibold" htmlFor="admin-chat-conversation-id">Conversation ID</label>
                    <NumericTextBox id="admin-chat-conversation-id" value={conversationId} min={1} format="n0" onChange={handleConversationIdChange} />
                </div>
                <div className="col-12 col-md-3">
                    <label className="form-label fw-semibold" htmlFor="admin-chat-customer">Customer</label>
                    <DropDownList id="admin-chat-customer" data={customerData?.data ?? []} textField="name" dataItemKey="id" value={selectedCustomer} onChange={handleCustomerChange} disabled={isCustomersFetching} defaultItem={{ id: 0, name: isCustomersFetching ? "Loading customers..." : "All customers" }} />
                </div>
                <div className="col-12 col-md-3">
                    <label className="form-label fw-semibold" htmlFor="admin-chat-user">User</label>
                    <DropDownList id="admin-chat-user" data={userData?.data ?? []} textField="email" dataItemKey="id" value={selectedUser} onChange={handleUserChange} disabled={selectedCustomer == null || isUsersFetching} defaultItem={{ id: "", email: isUsersFetching ? "Loading users..." : selectedCustomer == null ? "Select customer first" : "All users" }} />
                </div>
            </div>
            {(isError || isCustomersError || isUsersError) && <div className="alert alert-danger mb-0" role="alert">Unable to load conversations, customers, or users. Please try again.</div>}
            <AppGrid
                showLoader={isFetching || isCustomersFetching || isUsersFetching}
                dataItemKey="id"
                data={data?.data ?? []}
                sortable={true}
                filterable={false}
                resizable={true}
                initialState={dataState}
                onPageChange={(event: GridPageChangeEvent) => setDataState(previous => ({ ...previous, skip: event.page.skip, take: event.page.take }))}
                onDataStateChange={(event: GridDataStateChangeEvent) => setDataState({ ...event.dataState, skip: event.dataState.skip, take: event.dataState.take })}
                detail={ConversationMessages}
                sort={dataState.sort}
                total={data?.total ?? 0}
                skip={dataState.skip}
                take={dataState.take}
                defaultSort={initialSort}
                onSortChange={(event: GridSortChangeEvent) => setDataState(previous => ({ ...previous, sort: event.sort, skip: 0 }))}
                pageable={{ buttonCount: 3, pageSizes: [50, 100, 200] }}
                style={{ height: "100%" }}
            >
                <GridNoRecords>{!isFetching && <span className="text-muted">{isError ? "Unable to load conversations. Please try again." : "No conversations found for the selected filters."}</span>}</GridNoRecords>
                <GridColumn field="id" title="ID" width="100px" />
                <GridColumn field="customerName" title="Customer" cells={{ data: p => <td className="k-table-td">{(p.dataItem as AdminChatConversationDto).customer_name}</td> }} />
                <GridColumn field="userEmail" title="User" cells={{ data: p => <td className="k-table-td">{(p.dataItem as AdminChatConversationDto).user_email}</td> }} />
                <GridColumn field="messageCount" title="Messages" width="120px" cells={{ data: p => <td className="k-table-td text-end">{(p.dataItem as AdminChatConversationDto).message_count}</td> }} />
                <GridColumn field="createdAt" title="Created" width="220px" cells={{ data: DateCell<AdminChatConversationDto>("created_at") }} />
                <GridColumn field="lastMessageAt" title="Last Message" width="220px" cells={{ data: DateCell<AdminChatConversationDto>("last_message_at") }} />
            </AppGrid>
        </div>
    );
};

export default ChatConversationManagement;
